import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  CardActionArea,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Button
} from '@mui/material';
import {
  AccountTree as AccountTreeIcon,
  LocationOn as LocationIcon,
  Category as CategoryIcon,
  Settings as SettingsIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { forecastHierarchyService } from '../../services/forecastHierarchy.service';

const HierarchySelector = ({ onHierarchySelect, selectedHierarchyId = null }) => {
  const [hierarchies, setHierarchies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchHierarchies = async () => {
      try {
        setLoading(true);
        console.log('Fetching hierarchies...');
        const response = await forecastHierarchyService.getHierarchies();
        console.log('Hierarchy response:', response);
        
        if (response.status === 'success') {
          // The API returns data directly as an array, not nested under 'hierarchies'
          const hierarchies = response.data || [];
          console.log('Fetched hierarchies:', hierarchies);
          setHierarchies(hierarchies);
        } else {
          throw new Error(response.message || 'Failed to fetch hierarchies');
        }
      } catch (err) {
        console.error('Error fetching hierarchies:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchHierarchies();
  }, []);

  const handleHierarchyClick = (hierarchy) => {
    console.log('HierarchySelector: handleHierarchyClick called with:', hierarchy);
    console.log('HierarchySelector: onHierarchySelect function:', onHierarchySelect);
    onHierarchySelect(hierarchy);
  };

  const getHierarchyIcon = (hierarchyType) => {
    switch (hierarchyType?.toLowerCase()) {
      case 'location':
        return <LocationIcon />;
      case 'product':
        return <CategoryIcon />;
      case 'attribute':
        return <SettingsIcon />;
      default:
        return <AccountTreeIcon />;
    }
  };

  const getHierarchyColor = (hierarchyType) => {
    switch (hierarchyType?.toLowerCase()) {
      case 'location':
        return 'primary';
      case 'product':
        return 'secondary';
      case 'attribute':
        return 'success';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading hierarchies...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        <Typography variant="body2">
          Error loading hierarchies: {error}
        </Typography>
        <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
          Please check your API connection and try again.
        </Typography>
      </Alert>
    );
  }

  if (hierarchies.length === 0) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardHeader 
          title="No Hierarchies Available"
          subheader="No hierarchies found for this forecast run"
        />
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            No hierarchies are currently available. This could be because:
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 2 }}>
            <Typography component="li" variant="body2" color="text.secondary">
              No hierarchies have been configured for this tenant
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              The hierarchy data hasn't been populated for this forecast run
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              There's an issue with the API connection
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Available Hierarchies
      </Typography>
      <Grid container spacing={2}>
        {hierarchies.map((hierarchy) => (
          <Grid item xs={12} sm={6} md={4} key={hierarchy.hierarchy_id}>
            <Card 
              sx={{ 
                height: '100%',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                border: selectedHierarchyId === hierarchy.hierarchy_id ? 2 : 1,
                borderColor: selectedHierarchyId === hierarchy.hierarchy_id ? 'primary.main' : 'divider',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 3
                }
              }}
            >
              <CardActionArea 
                onClick={() => handleHierarchyClick(hierarchy)}
                sx={{ height: '100%', p: 0 }}
              >
                <CardHeader
                  avatar={getHierarchyIcon(hierarchy.hierarchy_type)}
                  title={hierarchy.hierarchy_name}
                  subheader={`Type: ${hierarchy.hierarchy_type || 'Unknown'}`}
                  titleTypographyProps={{ variant: 'h6' }}
                  subheaderTypographyProps={{ variant: 'body2' }}
                />
                <CardContent>
                  <Box sx={{ mb: 2 }}>
                    <Chip 
                      label={`${hierarchy.levels?.length || 0} Levels`}
                      size="small"
                      color={getHierarchyColor(hierarchy.hierarchy_type)}
                      variant="outlined"
                    />
                    {hierarchy.version && (
                      <Chip 
                        label={`v${hierarchy.version}`}
                        size="small"
                        variant="outlined"
                        sx={{ ml: 1 }}
                      />
                    )}
                    {hierarchy.is_active !== undefined && (
                      <Chip 
                        label={hierarchy.is_active ? 'Active' : 'Inactive'}
                        size="small"
                        color={hierarchy.is_active ? 'success' : 'error'}
                        variant="outlined"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Box>
                  
                  <Divider sx={{ my: 1 }} />
                  
                  <Box>
                    {hierarchy.hierarchy_description && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        {hierarchy.hierarchy_description}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" display="block">
                      <strong>Levels:</strong> {hierarchy.levels?.map(l => l.display_name || l.level_name).join(' → ') || 'None'}
                    </Typography>
                    {hierarchy.display_order && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        <strong>Display Order:</strong> {hierarchy.display_order}
                      </Typography>
                    )}
                  </Box>
                  
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<VisibilityIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleHierarchyClick(hierarchy);
                      }}
                      sx={{ 
                        minWidth: 100,
                        backgroundColor: getHierarchyColor(hierarchy.hierarchy_type) === 'primary' ? 'primary.main' : 
                                     getHierarchyColor(hierarchy.hierarchy_type) === 'secondary' ? 'secondary.main' :
                                     getHierarchyColor(hierarchy.hierarchy_type) === 'success' ? 'success.main' : 'default.main'
                      }}
                    >
                      View
                    </Button>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default HierarchySelector; 